// Tooltip — shared hover tooltip.
//
// Renders `content` in a portal with fixed positioning so it is never clipped
// by an ancestor's overflow/scroll container (the reason the older ad-hoc
// tooltips — DDS Draw, Available, cleared-dates, cash-management totals — all
// hand-rolled the same portal + getBoundingClientRect dance). This centralizes
// that logic.
//
// Variants:
//   - variant="dark"  → dark popup (calculation breakdowns). Default.
//   - variant="light" → white bordered popover (lists / tables).
// Alignment:
//   - align="center"  → centered over the trigger, with an optional arrow.
//   - align="start"   → left-aligned to the trigger, clamped to the viewport.
// Placement:
//   - placement="auto"   → above the trigger, flipping below only when cramped. Default.
//   - placement="bottom" → always below the trigger.
//   - placement="top"    → always above the trigger.
// interactive=true lets the pointer move into the tooltip (small close delay) —
// use for tooltips whose content the user needs to read slowly or scroll.
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function Tooltip({
  children,
  content,
  variant = "dark",
  align = "center",
  placement = "auto",
  interactive = false,
  arrow,
  width,
  contentClassName = "",
  triggerClassName = "",
  triggerAs: TriggerTag = "span",
  triggerProps = {},
  disabled = false,
}) {
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const [tip, setTip] = useState(null);

  const showArrow = arrow ?? (variant === "dark" && align === "center");
  const tooltipWidth = typeof width === "number" ? width : 280;

  const computePosition = useCallback(() => {
    const el = ref.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    // Placement: forced bottom/top, else auto — flip below only when cramped above.
    const below = placement === "bottom" ? true : placement === "top" ? false : rect.top < 180;
    let x;
    if (align === "center") {
      x = rect.left + rect.width / 2;
    } else {
      const maxLeft = Math.max(8, window.innerWidth - tooltipWidth - 8);
      x = Math.min(Math.max(8, rect.left), maxLeft);
    }
    return { x, y: below ? rect.bottom : rect.top, below };
  }, [align, tooltipWidth, placement]);

  const show = useCallback(() => {
    if (disabled || content == null) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const pos = computePosition();
    if (pos) setTip(pos);
  }, [disabled, content, computePosition]);

  const hide = useCallback(() => {
    if (interactive) {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => setTip(null), 90);
    } else {
      setTip(null);
    }
  }, [interactive]);

  const cancelHide = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const boxClass =
    variant === "dark"
      ? "rounded-lg bg-palette-gray-primary text-white text-xs px-4 py-3 shadow-xl"
      : "rounded border border-palette-slate-divider bg-white px-2 py-2 text-[11px] text-palette-slate-heading shadow-xl";

  return (
    <>
      <TriggerTag
        ref={ref}
        className={triggerClassName}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        {...triggerProps}
      >
        {children}
      </TriggerTag>
      {tip &&
        createPortal(
          <div
            style={{ left: tip.x, top: tip.y, ...(typeof width === "number" ? { width } : {}) }}
            className={`fixed z-[9999] ${align === "center" ? "-translate-x-1/2" : ""} ${
              tip.below ? "translate-y-2" : "-translate-y-full"
            } ${interactive ? "" : "pointer-events-none"}`}
            onMouseEnter={interactive ? cancelHide : undefined}
            onMouseLeave={interactive ? hide : undefined}
          >
            <div
              className={`${boxClass} ${typeof width === "number" ? "w-full" : ""} ${
                tip.below ? "mt-0" : "mb-2"
              } ${contentClassName}`}
            >
              {typeof content === "function" ? content() : content}
            </div>
            {showArrow && (
              <div
                className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${
                  tip.below
                    ? "-top-1.5 border-b-[6px] border-b-palette-gray-primary"
                    : "-bottom-1 border-t-[6px] border-t-palette-gray-primary"
                }`}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
